import { Injectable } from '@angular/core';
import { DefaultDataService, HttpUrlGenerator } from '@ngrx/data';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, Subject } from 'rxjs';
import { map, tap, concatMap } from 'rxjs/operators';
import { ApiResponse } from '@core/models/api-response.model';
import { Promotion } from '@core/models/promotion.model';
import { PromotionV2 } from '@core/models/promotion-v2.model';
import { Pagination } from '@core/models/pagination.model';
import { BlacklistedRequest } from '../dialogs/game-provider-new-blacklist/get-blacklisted-request.interface';
import { BlacklistedData } from '../dialogs/game-provider-new-blacklist/get-blacklisted-data.interface';
import { UpdateBlacklistedRequest } from '../dialogs/game-provider-new-blacklist/update-blacklisted-request.interface';
import { promotionDropdown } from '@core/models/promotion-dropdown.model';
@Injectable()
export class PromotionCodeDataService extends DefaultDataService<Promotion> {

  pagination: Pagination;
  messages$ = new Subject<any[]>();
  newBlacklistedGameProviders$ = new BehaviorSubject<any>(null);

  promotionTypes = [
    { value: 1, name: 'Percentage' },
    { value: 2, name: 'Fixed Amount' }
  ];

  constructor(http: HttpClient, httpUrlGenerator: HttpUrlGenerator) {
      super('Promotion', http, httpUrlGenerator, { root: '' });
  }

  getAll(): Observable<Promotion[]> {
    return this.http.get<ApiResponse>('/promotion').pipe(
      tap(res => this.paginationInit(res)),
      map(res => res.data.rows)
    );
  }

  getActive(): Observable<Promotion[]> {
    return this.http.get<ApiResponse>('/promotion?status=1&sort_by=id&sort_order=DESC').pipe(
      tap(res => this.paginationInit(res)),
      map(res => res.data.rows)
    );
  }

  add(contact?: Promotion): Observable<Promotion>{
    return this.http.post<ApiResponse>(`/promotion`, contact).pipe(
      tap( res => this.messages$.next(res.message)),
      concatMap((entity) => this.http.get(`/promotion/${entity.data.rows.id}`).pipe(
        map((row: ApiResponse) => row.data.rows)
      ))
    );
  }

  addv2(contact?: PromotionV2): Observable<PromotionV2>{
    return this.http.post<ApiResponse>(`/promotion/storev2`, contact).pipe(
      tap( res => this.messages$.next(res.message)),
      map(res => res.data.rows)
    );
  }

  addv3(contact?: PromotionV2): Observable<PromotionV2>{
    return this.http.post<ApiResponse>(`/promotion/storev3`, contact).pipe(
      tap( res => this.messages$.next(res.message)),
      map(res => res.data.rows)
    );
  }

  updatev2(data?: PromotionV2){
    return this.http.put<ApiResponse>(`/promotion/updatev2/${data.id}`, data).pipe(
      tap( res => this.messages$.next(res.message))
    );

    // return this.http.put<ApiResponse>(`/bankaccount/reupload-document/${id}`, data).pipe(
    //   tap(res => this.messages$.next(res.message))
    // );
  }

  updatev3(data?: PromotionV2){
    return this.http.put<ApiResponse>(`/promotion/updatev3/${data.id}`, data).pipe(
      tap( res => this.messages$.next(res.message))
    );
  }

  addv4(contact?: PromotionV2): Observable<PromotionV2>{
    return this.http.post<ApiResponse>(`/promotion/storev4`, contact).pipe(
      tap( res => this.messages$.next(res.message)),
      map(res => res.data.rows)
    );
  }

  updatev4(data?: PromotionV2){
    return this.http.put<ApiResponse>(`/promotion/updatev4/${data.id}`, data).pipe(
      tap( res => this.messages$.next(res.message))
    );
  }

  duplicate(promotion?: Promotion): Observable<Promotion>{
    return this.http.post<ApiResponse>(`/promotion/duplicate?${promotion.id}`, promotion).pipe(
      tap( res => this.messages$.next(res.message)),
      concatMap((entity) => this.http.get(`/promotion/${entity.data.rows.id}`).pipe(
        map((row: ApiResponse) => row.data.rows)
      ))
    );
  }

  getById(id: number): Observable<Promotion>{
    return this.http.get<ApiResponse>(`/promotion/${id}`).pipe(
      map(res => res.data.rows)
    );
  }

  getById2(id: number): Observable<Promotion>{
    return this.http.get<ApiResponse>(`/promotion/showv2/${id}`).pipe(
      map(res => res.data.rows)
    );
  }

  getById3(id: number): Observable<Promotion>{
    return this.http.get<ApiResponse>(`/promotion/showv3/${id}`).pipe(
      map(res => res.data.rows)
    );
  }

  getById4(id: number): Observable<Promotion>{
    return this.http.get<ApiResponse>(`/promotion/showv4/${id}`).pipe(
      map(res => res.data.rows)
    );
  }

  getWithQuery(pageParam: string): Observable<Promotion[]>{
    return this.http.get<ApiResponse>(`/promotion${pageParam}`).pipe(
      tap(res => this.paginationInit(res)),
      map(res => res.data.rows)
    )
  }

  export(pageParam: string) {
    return this.http.get<ApiResponse>(`/promotion/export${pageParam}`).pipe(
      tap(res => this.messages$.next(res.message))
    );
  }

  importMemberList(data: any, type: string){
    return this.http.post<ApiResponse>(`/promotion/bulkImportMember`, data).pipe(
      tap(res => { if (type === 'edit'){ this.messages$.next(res.message) } })
    );
  }

  getBlacklistedData(blacklistedRequest: BlacklistedRequest): Observable<BlacklistedData[]>{
    return this.http.post<ApiResponse>(`/promotion/blacklistgame`, blacklistedRequest).pipe(
      map(res => res.data.rows)
    )
  }

  updateBlacklistedData(updateBlacklistedRequest: UpdateBlacklistedRequest){
    return this.http.post<ApiResponse>(`/promotion/updateblacklistgame`, updateBlacklistedRequest);
  }

  getPromotionDropdown(): Observable<promotionDropdown[]> {
    return this.http.get<ApiResponse>(`/promotiondropdown`).pipe(
      map(res => res.data.rows)
    );
  }

  checkEligibility(promotion_id: number, member_id: number, is_reward: number, game_provider_code?: string) {
    return this.http.get<ApiResponse>(`/promotion/checkeligibility?promotion_id=${promotion_id}&member_account_id=${member_id}&is_reward=${is_reward}&game_provider_code=${game_provider_code}`).pipe(
      map(res => res)
    );
  }

  getPromotionDetailsByGameProvider(game_provider_id: number): Observable<any[]>{
    return this.http.get<ApiResponse>(`/promotion/getPromotionDetailsByGameProvider`, {
      params: { game_provider_id: game_provider_id.toString() }
    }).pipe(
      map(res => res.data.rows)
    )
  }

  updatePromotionByGameProvider(promotionDetails: any){
    return this.http.post<ApiResponse>(`/promotion/updatePromotionByGameProvider`, promotionDetails).pipe(
      tap( res => this.messages$.next(res.message))
    );
  }

  delete(id: number): Observable<any> {
    return this.http.delete<ApiResponse>(`/promotion/${id}`).pipe(
      tap(res => this.messages$.next(res.message))
    );
  }

  getArchived(pageParam: string = ''): Observable<Promotion[]> {
    return this.http.get<ApiResponse>(`/promotion/archived${pageParam}`).pipe(
      tap(res => this.paginationInit(res)),
      map(res => res.data.rows)
    );
  }

  restore(id: number): Observable<any> {
    return this.http.post<ApiResponse>(`/promotion/restore/${id}`, {}).pipe(
      tap(res => this.messages$.next(res.message))
    );
  }

  private paginationInit(res: any) {
    if(res) {
      this.pagination = res.data.paginations;
    }
  }

}
